import type { CDPResourceCollectionListItem } from '@src/app/app/[country]/[lang]/programs/[slug]/_components/CDPModules/CDPResourceCollectionListModule'
import type { MediaItem } from '@src/components/MediaTile/MediaTile.props'
import { getLocalizedUrl } from '@src/i18n/locale.util'
import type { LocaleConfig } from '@src/i18n/locales'
import {
  getDescription,
  getFooterText,
  getIsPlayButtonVisible,
  getStatisticsCopy,
} from '@src/mappers/media/media-display.helpers'
import { getVideoProgress } from '@src/mappers/media/video-history.helpers'
import { getEntitiesByIds } from '@src/utils/cocoa.util'
import { CocoaEntityType } from '@src/utils/constants.util'
import { buildShopifyProductUrl } from '@src/utils/url.util'

export const getContentBaseRoute = (type?: CocoApi.ContentType | null) => {
  const baseRoutes: Record<
    Exclude<CocoApi.ContentType, CocoApi.ContentType.customCollection>,
    string
  > = {
    program: '/app/programs',
    bodiBlock: '/app/blocks',
    compilation: '/app/content',
  }
  return type && type !== 'customCollection' ? baseRoutes[type] : null
}

export const getEquipmentNeededText = (
  equipmentRequired: CocoApi.Equipment[] = [],
  equipmentRecommended: CocoApi.Equipment[] = []
) => {
  const equipmentArray: string[] = []
  if (equipmentRequired?.length) {
    equipmentRequired.forEach((equipment) => {
      const title = equipment?.title
      if (title) equipmentArray.push(title)
    })
  }
  if (equipmentRecommended?.length) {
    equipmentRecommended.forEach((equipment) => {
      const title = equipment?.title
      if (title) equipmentArray.push(`${title}*`)
    })
  }
  return equipmentArray.join(', ')
}

const getParentUrl = (locale: LocaleConfig, primaryParent?: CocoApi.PrimaryParent) => {
  const parentContentBaseRoute = primaryParent?._type && getContentBaseRoute(primaryParent._type)

  return parentContentBaseRoute && primaryParent?.slug
    ? getLocalizedUrl(`${parentContentBaseRoute}/${primaryParent.slug}`, locale)
    : undefined
}

export enum CODE_PARAM {
  PDP = 'BODWEB_{brandcode}_PROGRAM',
  BODWEB_BODI_SEARCH = 'BODWEB_BODI_SEARCH',
  BODWEB_FITNESS_HUB = 'BODWEB_FITNESS_HUB',
}

const getBrandCode = (entity: CocoApi.PlayableEntity | CocoApi.CustomCollectionEntity) =>
  (entity as { brandCode?: string })?.brandCode

const getTrainerName = (entity: CocoApi.PlayableEntity | CocoApi.CustomCollectionEntity) => {
  const name = entity?.trainers?.map((t) => `${t.firstName ?? ''}${t.lastName ?? ''}`).join(' - ')
  return name ?? undefined
}

export const getOffersUrl = (
  item: CocoApi.PlayableEntity | CocoApi.CustomCollectionEntity,
  locale: LocaleConfig,
  code?: CODE_PARAM | string
) => {
  const productPath = locale.getSubscriptionPurchaseUrl()
  // '/' signals the locale has no subscription page — use the localized homepage.
  if (productPath === '/') return getLocalizedUrl('/', locale)

  const purchaseUrl = item.purchaseOptions?.[0]?.purchaseAction?.url
  const subscriptionUrl = buildShopifyProductUrl(locale.code, {
    productPath,
    includeEnglishLangSegment: true,
  })
  const baseUrl = getLocalizedUrl(purchaseUrl ?? subscriptionUrl, locale)

  try {
    const url = new URL(baseUrl)
    const brandCode = getBrandCode(item) ?? url.searchParams.get('referralProgramId') ?? undefined
    const trainerName = getTrainerName(item)
    const resolvedCode =
      code === CODE_PARAM.PDP
        ? brandCode
          ? CODE_PARAM.PDP.replace('{brandcode}', brandCode)
          : undefined
        : code

    if (locale?.codeSnakeCase) url.searchParams.set('locale', locale.codeSnakeCase)
    if (brandCode) url.searchParams.set('referralProgramId', brandCode)
    if (trainerName) url.searchParams.set('trainerName', trainerName)
    if (resolvedCode) url.searchParams.set('code', resolvedCode)

    return url.toString()
  } catch {
    return baseUrl
  }
}

export const getWorksWellForStamps = (dietaryGroups?: CocoApi.DietaryGroup[]) =>
  dietaryGroups?.map((group: CocoApi.DietaryGroup) => ({
    title: group.title ?? '',
    colors: group.colors ?? {},
    abbreviation: group.label ?? '',
  }))

const getShareUrl = (item: CocoApi.CustomCollectionEntity, locale: LocaleConfig) => {
  const parentUrl = getParentUrl(locale, item.primaryParent)

  if (!parentUrl || !item.video?.videoId || typeof window === 'undefined') return undefined

  const { host, protocol } = new URL(window.location.href)
  const absoluteURL = new URL(`${protocol}//${host}${parentUrl}`)

  if (item.slug) {
    absoluteURL.searchParams.set('slug', item.slug)
  } else if (item.video?.videoId) {
    absoluteURL.searchParams.set('videoGuid', item.video.videoId)
  }
  return absoluteURL.toString()
}

export const cocoaEntityToMediaRailItem = (
  item: CocoApi.CustomCollectionEntity,
  locale: LocaleConfig,
  /** Track day the item is being shown for; scopes its watch history. */
  dayId?: string
): MediaItem => ({
  slug: item.slug,
  videoId: item.video?.videoId,
  title: item.title,
  href: item.redirectUrl,
  durationEstimate: item.durationEstimate,
  entityId: item._id,
  entityType: item._type!,
  violator: item.stamps?.[0]?.title,
  imageUrl: item.backgroundImage ?? '',
  description:
    getDescription(item.durationEstimate ?? item.video?.durationActual, item.trainers) ??
    item.description,
  footerText: getFooterText(item.subcategories, item.categories),
  progress: getVideoProgress(item, dayId),
  isPlayable: getIsPlayButtonVisible(item.video, item.isPlaceholder, !item.userData?.isEntitled),
  isLocked: !item.userData?.isEntitled,
  isFavorited: item.userData?.isFavorited ?? false,
  isFavoritable: item.isFavoritable ?? false,
  shareUrl: getShareUrl(item, locale),
  parentUrl: getParentUrl(locale, item.primaryParent),
  opensPanelOnThumbnailClick: item._type === CocoaEntityType.recipe,
  alwaysShowLockIcon: !!item.video && !item.isPlaceholder && !item.userData?.isEntitled,
  worksWellForStamps: getWorksWellForStamps(item.dietaryGroups),
})

export const cocoaEntityToMediaGridItem = (
  item: CocoApi.CustomCollectionEntity,
  locale: LocaleConfig,
  dayId?: string
): MediaItem => ({
  ...cocoaEntityToMediaRailItem(item, locale, dayId),
  descriptionLong: item.descriptionLong,
  programStatistics: getStatisticsCopy(item.programStatistics),
})

export const resourceEntityToResourceCollectionListItem = (
  entity: CocoApi.ResourceEntity,
  purchaseUrl: string,
  parentId?: string
): CDPResourceCollectionListItem => ({
  title: entity.title ?? '',
  slug: entity.slug ?? '',
  isLocked: !entity.userData?.isEntitled,
  imageUrl: entity.thumbnail?.url ?? '',
  purchaseUrl,
  fileUrl: entity?.file?.url ?? (entity as CocoApi.RecipeEntity).recipeFile?.url,
  linkUrl: entity?.link ?? (entity as CocoApi.RecipeEntity).recipeLink,
  resourceId: entity._id,
  resourceType: entity.file?.url ? 'file' : 'url',
  resourceUrl: entity.file?.url ?? entity.link ?? '',
  parentId,
  primaryParentId: entity.primaryParent?._id ?? parentId,
})

export const cocoaEntityToMediaPanelItem = (
  item: CocoApi.CustomCollectionEntity | CocoApi.PlayableEntity,
  parentContent: CocoApi.Program | CocoApi.Compilation | CocoApi.BODiBlock | null | undefined,
  locale: LocaleConfig,
  code?: CODE_PARAM | string,
  dayId?: string,
  hideParentProgramLink = false
): MediaItem => ({
  ...cocoaEntityToMediaGridItem(item as CocoApi.CustomCollectionEntity, locale, dayId),
  parentUrl: hideParentProgramLink ? undefined : getParentUrl(locale, item.primaryParent),
  equipmentNeededText: getEquipmentNeededText(item.equipmentRequired, item.equipmentRecommended),
  worksWellForStamps: getWorksWellForStamps(item.dietaryGroups),
  parentLinkText: hideParentProgramLink
    ? undefined
    : (item.primaryParent?.title ??
      parentContent?.title ??
      (item as CocoApi.CustomCollectionEntity).program?.title),
  purchaseUrl: getOffersUrl(item, locale, code),
  imageUrl: item.thumbnail?.url ?? (item as CocoApi.CustomCollectionEntity).backgroundImage ?? '',
  recipeFileUrl: item.recipeFile?.url,
  recipeLinkUrl: (item as CocoApi.RecipeEntity).recipeLink,
  associatedContent: getEntitiesByIds<CocoApi.ResourceEntity>(
    item.associatedContent?.entityIds,
    Object.values(item.associatedContent?.entities ?? parentContent?.entities ?? {})
  ),
  primaryParentId: item.primaryParent?._id,
  nutritionPlanBucket: item.nutritionPlanBucket,
  playCtaLabelKey: CocoaEntityType.workout === item._type ? 'start-workout' : 'watch-video',
})
